import { push } from "connected-react-router";
import * as R from "ramda";
import { zip } from "ramda";
import { call, put, select } from "redux-saga/effects";
import { ActionCreator, IAction } from "reduxsauce";
import { AxiosInterceptedResult } from "ts/api/interceptors";
import { IResolveResponse } from "ts/api/ResolveApi/resolve-api.types";
import { ICourse } from "../App/types/common";
import { modifyObjectDeletedPatch, patchPayloadCreator } from "../components/course-author/helpers/patches/creators";
import { api } from "../dependencies";
import {
  filterSuccessfulResponses,
  hasErrorRecords,
  ICourseUIData,
  resolveCoursePostSuccessResponseValueToCourseUIData,
} from "../parsers/resolve";
import { SagaAction } from "../sagas/types";
import * as selectors from "../selectors/selectors";
import { Creators as AllCreators } from "./";
import { ICourseIdAction } from "./course-author/course-author";
import { createNewCourse } from "./helpers/course";
import { SagaFunction } from "./types";

export const Creators = (AllCreators as any).App.CourseAuthor;
export const OrgCreators = (AllCreators as any).App.SelectOrganization;

export interface IFetchAllCoursesFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export function* fetchAndResolveCourses(accountId: string, organizationId: string) {
  const [courseIdsError, courseIdsResponse]: AxiosInterceptedResult<string[]> = yield call(
    api.courses.getEditableByRelativeTo,
    accountId,
    organizationId
  );
  if (courseIdsError || !courseIdsResponse) {
    return [{ message: "Failed to fetch courses." }, null];
  } else {
    const [coursesError, coursesResponse]: AxiosInterceptedResult<Array<IResolveResponse<ICourse>>> = yield call(
      api.resolve.course.post,
      accountId,
      courseIdsResponse.data
    );
    if (coursesError || !coursesResponse) {
      return [{ message: "Failed to fetch courses." }, null];
    } else if (hasErrorRecords(coursesResponse.data)) {
      const correctRecords = R.fromPairs(filterSuccessfulResponses(courseIdsResponse.data, coursesResponse.data));
      return [
        null,
        {
          courseIdsResponse: { data: Object.keys(correctRecords) },
          coursesResponse: { data: Object.values(correctRecords) },
        },
      ];
    } else {
      return [null, { courseIdsResponse, coursesResponse }];
    }
  }
}

export const fetchAllCourses: (furtherActions: IFetchAllCoursesFurtherActions) => SagaFunction = furtherActions =>
  function* _fetchAllCourses(_: IAction): SagaAction {
    const accountId = yield select(selectors.accountId);
    const organizationId = yield select(selectors.organizationId);
    const [coursesError, response] = yield call(fetchAndResolveCourses, accountId, organizationId);
    if (coursesError) {
      return yield put(furtherActions.onFailure(coursesError));
    }
    const {
      courseIdsResponse,
      coursesResponse,
    }: {
      courseIdsResponse: { data: string[] };
      coursesResponse: { data: ICourseUIData[] };
    } = response;
    const data = zip(courseIdsResponse.data, coursesResponse.data).map(([uuid, datum]) => ({
      ...resolveCoursePostSuccessResponseValueToCourseUIData(datum),
      courseId: uuid,
    }));
    yield put(furtherActions.onSuccess(data));
  };

export interface IFetchCourseFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const fetchCourse: (furtherActions: IFetchCourseFurtherActions) => SagaFunction = furtherActions =>
  function* _fetchCourse(action: IAction): SagaAction {
    const accountId = yield select(selectors.accountId);
    const [coursesError, coursesResponse] = yield call(api.resolve.course.post, accountId, [action.courseId]);
    if (coursesError) {
      yield put(furtherActions.onFailure({ message: "Failed to fetch course by id." }));
    } else if (hasErrorRecords(coursesResponse.data)) {
      yield put(furtherActions.onFailure({ message: "Course contained errors." }));
    } else {
      const data = zip([action.courseId], coursesResponse.data).map(([uuid, datum]) => ({
        ...resolveCoursePostSuccessResponseValueToCourseUIData(datum),
        courseId: uuid,
      }));
      yield put(furtherActions.onSuccess(data[0]));
    }
  };

export interface ICreateCourseFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const createCourse: (fa: ICreateCourseFurtherActions) => SagaFunction = fa =>
  function* _createCourse(_: IAction): SagaAction {
    const accountId = yield select(selectors.accountId);
    const organizationId = yield select(selectors.organizationId);
    const newCourse = yield call(createNewCourse, accountId, organizationId);
    const [newCourseError, newCourseResponse] = yield call(api.courses.createCourse, accountId, newCourse);
    if (newCourseError) {
      yield put(fa.onFailure({ message: "Failed to create new course" }));
    } else {
      yield put(fa.onSuccess({}));
      yield put(OrgCreators.updateCoursesAmount(1));
      yield put(push(`/course-author/${newCourseResponse.data.value}`));
    }
  };

export interface IDeleteCourseFurtherActions {
  onSuccess: ActionCreator;
  onFailure: ActionCreator;
}

export const deleteCourse: (fa: IDeleteCourseFurtherActions) => SagaFunction = fa => {
  return function* _deleteCourse(action: ICourseIdAction): SagaAction {
    const { courseId } = action;
    const accountId = yield select(selectors.accountId);
    const oldCourses: ICourse[] = yield select(selectors.courses);
    const coursesForDelete: ICourse[] = oldCourses.filter(c => c.courseId === courseId);
    if (coursesForDelete.length === 0) {
      return yield put(fa.onFailure({ message: "Course requested for delete not found." }));
    }
    const versionId = coursesForDelete[0].latestRevId;
    const deletePatch = yield call(modifyObjectDeletedPatch, versionId, accountId);
    const patchPayload = yield call(patchPayloadCreator, versionId, deletePatch);
    const [patchError] = yield call(api.courseAuthor.patchCourse, accountId, courseId, patchPayload);
    if (patchError) {
      return yield put(
        fa.onFailure({
          message: "Failed to patch course as deleted.",
        })
      );
    }
    const courses = oldCourses.filter(c => c.courseId !== courseId);
    yield put(fa.onSuccess(courses));
    yield put(OrgCreators.updateCoursesAmount(-1));
  };
};
